import React, { useCallback, useEffect, useRef } from 'react';
import type {
  CatalogInteraction,
  CatalogNotifier,
} from '@components/ui/catalog/ResourceCatalog/ResourceCatalog.interactions';
import { useOrganizationId } from '@features/user';
import { Alert, Button } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useTypeCatalogData } from '../hooks/useTypeCatalogData';

import { AppointmentTypeCatalog } from './AppointmentTypeCatalog';

/** Search filter edits are keystroke-level — debounce before tracking. */
const SEARCH_TRACK_DEBOUNCE_MS = 1500;

/**
 * Live container for the evergreen catalog (GDN-2335 phase 2): bootstrap
 * read + batch-archive verbs via useTypeCatalogData, Statsig tracking on the
 * catalog's onAction/onInteraction seams. The hosting page owns the feature
 * gate, the permission check, and the header actions (Manage / wizard).
 */
export const AppointmentTypeCatalogContainer: React.FC = () => {
  const organizationId = useOrganizationId() ?? undefined;
  const { typeCatalog } = useEventTracking();
  const { types, isLoading, isError, refetch, verbs } =
    useTypeCatalogData(organizationId);

  // One opened event per mount, once the data is actually in front of the
  // admin (counts are the payload — no point firing while loading).
  const openedRef = useRef(false);
  useEffect(() => {
    if (openedRef.current || isLoading || isError || types.length === 0) {
      return;
    }
    openedRef.current = true;
    typeCatalog.opened({
      organization_id: organizationId,
      total_types: types.length,
      archived_count: types.filter((t) => t.archived).length,
      shared_count: types.filter((t) => t.owner_scope === 'organization')
        .length,
      personal_count: types.filter((t) => t.owner_scope !== 'organization')
        .length,
    });
  }, [types, isLoading, isError, organizationId, typeCatalog]);

  const searchTrackTimer = useRef<number | null>(null);
  useEffect(
    () => (): void => {
      if (searchTrackTimer.current !== null) {
        window.clearTimeout(searchTrackTimer.current);
      }
    },
    []
  );

  const handleInteraction = useCallback(
    (interaction: CatalogInteraction): void => {
      switch (interaction.type) {
        case 'filter_changed': {
          const metadata = {
            organization_id: organizationId,
            scope: interaction.filters.scope,
            owners_count: interaction.filters.owners.length,
            has_search: interaction.filters.search.trim() !== '',
            show_archived: interaction.filters.showArchived,
          };
          // Search edits arrive per keystroke — debounce those; fire
          // structural filter changes (scope/owner/archived) immediately.
          if (searchTrackTimer.current !== null) {
            window.clearTimeout(searchTrackTimer.current);
            searchTrackTimer.current = null;
          }
          if (metadata.has_search) {
            searchTrackTimer.current = window.setTimeout(() => {
              typeCatalog.filterChanged(metadata);
            }, SEARCH_TRACK_DEBOUNCE_MS);
          } else {
            typeCatalog.filterChanged(metadata);
          }
          return;
        }
        case 'sort_changed':
          typeCatalog.sortChanged({
            organization_id: organizationId,
            sort_key: interaction.sort.key,
            direction: interaction.sort.direction,
          });
          return;
        case 'row_expanded':
          typeCatalog.rowDefaultsOpened({
            organization_id: organizationId,
            encounter_type_id: interaction.id,
          });
          return;
        case 'row_collapsed':
          // Collapse is not tracked ("_collapsed" isn't an allowlisted
          // suffix, and the open is the signal that matters).
          return;
        case 'undo_clicked':
          typeCatalog.undoClicked({
            organization_id: organizationId,
            action: interaction.action,
            count: interaction.ids.length,
          });
          return;
      }
    },
    [organizationId, typeCatalog]
  );

  const handleAction = useCallback(
    (action: 'archive' | 'restore', ids: string[]): void => {
      typeCatalog.actionSubmitted({
        organization_id: organizationId,
        action,
        count: ids.length,
      });
    },
    [organizationId, typeCatalog]
  );

  // Verb-outcome toasts through the app's notistack (same look as every
  // other notification) instead of the catalog's built-in fallback
  // Snackbar. Undo dismisses the toast, then runs the inverse verb.
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const notifier = useCallback<CatalogNotifier>(
    ({ severity, message, onUndo }) => {
      enqueueSnackbar(message, {
        variant: severity,
        autoHideDuration: 5000,
        action: onUndo
          ? (snackbarKey): React.ReactNode => (
              <Button
                color="inherit"
                size="small"
                onClick={(): void => {
                  closeSnackbar(snackbarKey);
                  onUndo();
                }}
              >
                Undo
              </Button>
            )
          : undefined,
      });
    },
    [enqueueSnackbar, closeSnackbar]
  );

  if (isError) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        Your appointment types could not be loaded.
      </Alert>
    );
  }

  return (
    <AppointmentTypeCatalog
      types={types}
      verbs={verbs}
      loading={isLoading}
      onAction={handleAction}
      onInteraction={handleInteraction}
      notifier={notifier}
    />
  );
};
